import React, { useState, useEffect } from "react";
import toast from "../../utils/toast";

export default function EnquiryFormModal({
  show,
  onClose,
  enquiry, // if null => Create mode, if object => Edit mode
  onSuccess,
}) {
  const API_URL = process.env.REACT_APP_BASE_URL_API || "";
  const token = localStorage.getItem("adminToken");

  const isEdit = Boolean(enquiry && enquiry._id);

  const [formData, setFormData] = useState({
    name: "",
    mobile: "",
    email: "",
    enquiry_for: "Shaadi / Matchmaking",
    custom_enquiry_for: "",
    message: "",
  });

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errors, setErrors] = useState({});

  useEffect(() => {
    if (enquiry && enquiry._id) {
      const predefinedTopics = [
        "Shaadi / Matchmaking",
        "Membership Plan",
        "Profile Verification",
        "Event Sponsorship",
        "Technical Support",
        "General Enquiry",
      ];
      const isCustom = !predefinedTopics.includes(enquiry.enquiry_for || "");

      setFormData({
        name: enquiry.name || "",
        mobile: enquiry.mobile || "",
        email: enquiry.email || "",
        enquiry_for: isCustom ? "Other" : enquiry.enquiry_for || "General Enquiry",
        custom_enquiry_for: isCustom ? enquiry.enquiry_for || "" : "",
        message: enquiry.message || "",
      });
    } else {
      setFormData({
        name: "",
        mobile: "",
        email: "",
        enquiry_for: "Shaadi / Matchmaking",
        custom_enquiry_for: "",
        message: "",
      });
    }
    setErrors({});
  }, [enquiry, show]);

  if (!show) return null;

  const validate = () => {
    const newErrors = {};
    if (!formData.name.trim()) newErrors.name = "Full name is required";
    if (!formData.mobile.trim()) {
      newErrors.mobile = "Mobile number is required";
    } else if (!/^\d{10}$/.test(formData.mobile.trim())) {
      newErrors.mobile = "Mobile number must be exactly 10 digits";
    }

    if (!formData.email.trim()) {
      newErrors.email = "Email address is required";
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email.trim())) {
      newErrors.email = "Enter a valid email address";
    }

    if (formData.enquiry_for === "Other" && !formData.custom_enquiry_for.trim()) {
      newErrors.custom_enquiry_for = "Please specify the enquiry topic";
    }

    if (!formData.message.trim()) {
      newErrors.message = "Enquiry message is required";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validate()) return;

    try {
      setIsSubmitting(true);
      const enquiryForValue =
        formData.enquiry_for === "Other"
          ? formData.custom_enquiry_for.trim()
          : formData.enquiry_for;

      const payload = {
        name: formData.name.trim(),
        email: formData.email.trim(),
        mobile: formData.mobile.trim(),
        enquiry_for: enquiryForValue,
        message: formData.message.trim(),
      };

      const url = isEdit
        ? `${API_URL}/api/admin/enquiry-list/update/${enquiry._id}`
        : `${API_URL}/api/admin/enquiry-create`;

      const res = await fetch(url, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(payload),
      });

      const result = await res.json();

      if (result.success) {
        toast.showSuccess(
          result.message ||
          (isEdit ? "Enquiry updated successfully" : "Enquiry created successfully")
        );
        onClose();
        if (onSuccess) onSuccess();
      } else {
        toast.showError(result.message || "Failed to save enquiry");
      }
    } catch (err) {
      console.error("Save Enquiry Error:", err);
      toast.showError("Server error while saving enquiry");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <>
      <div
        className="modal fade show d-block"
        tabIndex="-1"
        role="dialog"
        style={{ backgroundColor: "rgba(0, 0, 0, 0.55)", zIndex: 1060 }}
        onClick={onClose}
      >
        <div
          className="modal-dialog modal-dialog-centered modal-lg"
          role="document"
          onClick={(e) => e.stopPropagation()}
        >
          <div className="modal-content border-0 shadow-lg rounded-3">
            {/* Header */}
            <div
              className="modal-header text-white"
              style={{
                background: "linear-gradient(135deg, #bf3272 0%, #852d91 100%)",
                borderTopLeftRadius: "calc(0.3rem - 1px)",
                borderTopRightRadius: "calc(0.3rem - 1px)",
              }}
            >
              <h5 className="modal-title font-weight-bold d-flex align-items-center">
                <i className={`fa ${isEdit ? "fa-pencil-square-o" : "fa-plus-circle"} mr-2`}></i>
                {isEdit ? "Edit Enquiry" : "Add New Enquiry"}
              </h5>
              <button
                type="button"
                className="close text-white"
                onClick={onClose}
                aria-label="Close"
                style={{ opacity: 0.9, fontSize: "1.4rem" }}
              >
                <span aria-hidden="true">&times;</span>
              </button>
            </div>

            {/* Form */}
            <form onSubmit={handleSubmit}>
              <div className="modal-body p-4" style={{ backgroundColor: "#fdfdfd" }}>
                <div className="row g-3">
                  {/* Name */}
                  <div className="col-12 col-md-6 mb-3">
                    <label className="font-weight-bold small text-dark mb-1">
                      Full Name <span className="text-danger">*</span>
                    </label>
                    <div className="input-group">
                      <div className="input-group-prepend">
                        <span className="input-group-text bg-light text-muted">
                          <i className="fa fa-user"></i>
                        </span>
                      </div>
                      <input
                        type="text"
                        className={`form-control ${errors.name ? "is-invalid" : ""}`}
                        placeholder="e.g. John Doe"
                        value={formData.name}
                        onChange={(e) =>
                          setFormData({ ...formData, name: e.target.value })
                        }
                      />
                      {errors.name && (
                        <div className="invalid-feedback">{errors.name}</div>
                      )}
                    </div>
                  </div>

                  {/* Mobile */}
                  <div className="col-12 col-md-6 mb-3">
                    <label className="font-weight-bold small text-dark mb-1">
                      Mobile Number <span className="text-danger">*</span>
                    </label>
                    <div className="input-group">

                      <input
                        type="tel"
                        maxLength="10"
                        className={`form-control ${errors.mobile ? "is-invalid" : ""}`}
                        placeholder="10 digit mobile number"
                        value={formData.mobile}
                        onChange={(e) =>
                          setFormData({
                            ...formData,
                            mobile: e.target.value.replace(/\D/g, ""),
                          })
                        }
                      />
                      {errors.mobile && (
                        <div className="invalid-feedback">{errors.mobile}</div>
                      )}
                    </div>
                  </div>

                  {/* Email */}
                  <div className="col-12 col-md-6 mb-3">
                    <label className="font-weight-bold small text-dark mb-1">
                      Email Address <span className="text-danger">*</span>
                    </label>
                    <div className="input-group">
                      <div className="input-group-prepend">
                        <span className="input-group-text bg-light text-muted">
                          <i className="fa fa-envelope"></i>
                        </span>
                      </div>
                      <input
                        type="email"
                        className={`form-control ${errors.email ? "is-invalid" : ""}`}
                        placeholder="user@example.com"
                        value={formData.email}
                        onChange={(e) =>
                          setFormData({ ...formData, email: e.target.value })
                        }
                      />
                      {errors.email && (
                        <div className="invalid-feedback">{errors.email}</div>
                      )}
                    </div>
                  </div>

                  {/* Enquiry For Topic */}
                  <div className="col-12 col-md-6 mb-3">
                    <label className="font-weight-bold small text-dark mb-1">
                      Enquiry For / Category <span className="text-danger">*</span>
                    </label>
                    <select
                      className="form-control"
                      value={formData.enquiry_for}
                      onChange={(e) =>
                        setFormData({ ...formData, enquiry_for: e.target.value })
                      }
                    >
                      <option value="Shaadi / Matchmaking">Shaadi / Matchmaking</option>
                      <option value="Membership Plan">Membership Plan</option>
                      <option value="Profile Verification">Profile Verification</option>
                      <option value="Event Sponsorship">Event Sponsorship</option>
                      <option value="Technical Support">Technical Support</option>
                      <option value="General Enquiry">General Enquiry</option>
                      <option value="Other">Other (Specify below)</option>
                    </select>
                  </div>

                  {/* Custom Topic if Other */}
                  {formData.enquiry_for === "Other" && (
                    <div className="col-12 mb-3">
                      <label className="font-weight-bold small text-dark mb-1">
                        Specific Topic <span className="text-danger">*</span>
                      </label>
                      <input
                        type="text"
                        className={`form-control ${errors.custom_enquiry_for ? "is-invalid" : ""
                          }`}
                        placeholder="Enter enquiry category or subject..."
                        value={formData.custom_enquiry_for}
                        onChange={(e) =>
                          setFormData({
                            ...formData,
                            custom_enquiry_for: e.target.value,
                          })
                        }
                      />
                      {errors.custom_enquiry_for && (
                        <div className="invalid-feedback">
                          {errors.custom_enquiry_for}
                        </div>
                      )}
                    </div>
                  )}

                  {/* Message */}
                  <div className="col-12 mb-2">
                    <label className="font-weight-bold small text-dark mb-1">
                      Enquiry Message <span className="text-danger">*</span>
                    </label>
                    <textarea
                      rows="4"
                      className={`form-control ${errors.message ? "is-invalid" : ""}`}
                      placeholder="Enter the complete enquiry or notes..."
                      value={formData.message}
                      onChange={(e) =>
                        setFormData({ ...formData, message: e.target.value })
                      }
                    ></textarea>
                    {errors.message && (
                      <div className="invalid-feedback">{errors.message}</div>
                    )}
                  </div>
                </div>
              </div>

              {/* Footer */}
              <div className="modal-footer bg-light border-top">
                <button
                  type="button"
                  className="btn btn-secondary btn-sm"
                  onClick={onClose}
                  disabled={isSubmitting}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="btn btn-sm text-white"
                  style={{ backgroundColor: "#bf3272", borderColor: "#bf3272", minWidth: "120px" }}
                  disabled={isSubmitting}
                >
                  {isSubmitting ? (
                    <>
                      <i className="fa fa-spinner fa-spin mr-1"></i> Saving...
                    </>
                  ) : (
                    <>
                      <i className="fa fa-check mr-1"></i> {isEdit ? "Update Enquiry" : "Create Enquiry"}
                    </>
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>
      <div className="modal-backdrop fade show"></div>
    </>
  );
}
